import { alpha, createTheme } from "@mui/material/styles";
import type { PaletteOptions } from "@mui/material/styles";
import { esES } from "@mui/material/locale";

/**
 * Design tokens copied 1:1 from `business-process-manager/project/assets/lendia.css`
 * (Lendia Design System — Token System v2), cross-referenced against how
 * `business-process-manager/project/assets/bpm.css` actually applies them to real
 * UI (topbar, sidebar, cards, tabs, tables, forms). Built from scratch — this file
 * does not reuse the `main1-6`/`secondary1-10` convention of the other theme files.
 */
const color = {
    // Action / brand
    coral500: "#F26649", // --color-action-primary
    coral600: "#E05438", // --color-action-primary-hover
    coral100: "#FDECEA", // --primitive-coral-100

    navy800: "#002652", // --color-brand-navy / --color-text-primary
    navy700: "#132B52", // --primitive-navy-700
    navy500: "#2B60B8", // --primitive-navy-500
    navy400: "#528CD6", // --color-brand-blue / --color-action-secondary
    navy300: "#A3D4E8", // --color-brand-sky
    navy100: "#E3F2F8", // --color-status-info-bg / --primitive-navy-100

    green600: "#0D9373", // --color-status-success-fg
    green500Hover: "#01876F", // hover real de `.btn.primary:hover` en bpm.css
    green100: "#D1F5EF", // --color-status-success-bg

    amber500: "#D97706", // --color-status-warning-fg
    amber100: "#FEF3C7", // --color-status-warning-bg

    red600: "#B91C1C", // --primitive-red-600
    red500: "#DC2626", // --color-status-danger-fg
    red100: "#FEE2E2", // --color-status-danger-bg

    slate500: "#7E90A4", // --color-text-secondary / --primitive-slate-400
    slate300: "#D5E3EC", // --color-border-default / --color-text-disabled
    slate200: "#E7F0F7", // --primitive-slate-200
    slate100: "#F3F7FA", // --color-bg-canvas
    slate50: "#F9FAFB", // --primitive-slate-50

    white: "#FFFFFF",
};

const shadow = {
    sm: "0px 1px 3px 0px rgba(0,38,82,0.08)",
    md: "0px 4px 12px 0px rgba(0,38,82,0.10)",
    lg: "0px 8px 24px 0px rgba(0,38,82,0.12)",
    card: "0px 5px 16px 0px rgba(0,38,82,0.08)",
    focus: "0 0 0 3px rgba(82,140,214,0.20)",
};

const radius = {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 999,
};

const fontDisplay = "'Outfit', system-ui, sans-serif";
const fontUi = "'Work Sans', system-ui, sans-serif";
const fontBody = "'Plus Jakarta Sans', system-ui, sans-serif";

export const bpmV2Theme = createTheme(
    {
        cssVariables: true,
        colorSchemes: {
            light: {
                palette: {
                    primary: {
                        main: color.coral500,
                        dark: color.coral600,
                        light: color.coral100,
                        contrastText: color.white,
                    },
                    secondary: {
                        main: color.navy400,
                        light: color.navy300,
                        dark: color.navy700,
                        contrastText: color.white,
                    },
                    error: {
                        main: color.red500,
                        light: color.red100,
                        dark: color.red600,
                        contrastText: color.white,
                    },
                    warning: {
                        main: color.amber500,
                        light: color.amber100,
                        contrastText: color.white,
                    },
                    success: {
                        main: color.green600,
                        light: color.green100,
                        dark: color.green500Hover,
                        contrastText: color.white,
                    },
                    info: {
                        main: color.navy400,
                        light: color.navy100,
                        dark: color.navy500,
                        contrastText: color.white,
                    },
                    text: {
                        primary: color.navy800,
                        secondary: color.slate500,
                        disabled: color.slate300,
                    },
                    background: {
                        default: color.slate100,
                        paper: color.white,
                    },
                    divider: color.slate300,
                    action: {
                        hover: alpha(color.navy400, 0.04),
                        selected: alpha(color.navy400, 0.1),
                        focus: alpha(color.navy400, 0.16),
                        disabledBackground: color.slate200,
                    },
                    // `PaletteOptions` is augmented globally (by the other files under
                    // src/theme/) with legacy `main1-6`/`secondary1-10` keys that this
                    // from-scratch theme intentionally does not use — cast past that
                    // ambient, unrelated requirement instead of fabricating those slots.
                } as unknown as PaletteOptions,
            },
        },
        shape: {
            borderRadius: radius.md,
        },
        typography: {
            // <body> en lendia.css usa --font-ui (Work Sans) como base
            fontFamily: fontUi,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            h1: {
                fontFamily: fontDisplay,
                fontWeight: 600,
                fontSize: "3rem",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
            },
            h2: {
                fontFamily: fontDisplay,
                fontWeight: 600,
                fontSize: "2.25rem",
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
            },
            h3: { fontFamily: fontDisplay, fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.2 },
            h4: { fontFamily: fontDisplay, fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.5 },
            h5: { fontFamily: fontDisplay, fontWeight: 500, fontSize: "1.125rem", lineHeight: 1.5 },
            h6: { fontFamily: fontDisplay, fontWeight: 600, fontSize: "1rem", lineHeight: 1.5 },
            subtitle1: { fontFamily: fontUi, fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.4 },
            subtitle2: {
                fontFamily: fontUi,
                fontWeight: 700,
                fontSize: "0.6875rem",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                lineHeight: 1.4,
            },
            body1: { fontFamily: fontUi, fontWeight: 400, fontSize: "0.875rem", lineHeight: 1.5 },
            body2: { fontFamily: fontUi, fontWeight: 400, fontSize: "0.8125rem", lineHeight: 1.5 },
            // Dense data labels (--text-data: 10px), used across bpm.css charts/tables
            // e.g. `.bar-x`, `.chart-canvas .grid-y span`.
            body3: { fontFamily: fontUi, fontWeight: 400, fontSize: "0.625rem", lineHeight: 1.4 },
            caption: { fontFamily: fontBody, fontWeight: 400, fontSize: "0.75rem", lineHeight: 1.7 },
            overline: {
                fontFamily: fontUi,
                fontWeight: 700,
                fontSize: "0.6875rem",
                letterSpacing: "0.07em",
                lineHeight: 1.5,
            },
            button: {
                fontFamily: fontDisplay,
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                letterSpacing: "-0.005em",
            },
        },
        shadows: [
            "none",
            shadow.sm,
            shadow.sm,
            shadow.card,
            shadow.card,
            shadow.md,
            shadow.md,
            shadow.md,
            shadow.md,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
            shadow.lg,
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    // Outfit ya se carga globalmente en src/App.css; Work Sans y Plus
                    // Jakarta Sans (usadas por --font-ui y --font-body) no están cargadas
                    // en ningún otro lado de la app, así que este theme se auto-provee.
                    "@import":
                        "url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400&display=swap')",
                    body: {
                        backgroundColor: color.slate100,
                    },
                    "*::-webkit-scrollbar": {
                        width: 8,
                        height: 8,
                    },
                    "*::-webkit-scrollbar-track": {
                        background: "transparent",
                    },
                    "*::-webkit-scrollbar-thumb": {
                        background: color.slate300,
                        borderRadius: radius.pill,
                    },
                    "*::-webkit-scrollbar-thumb:hover": {
                        background: color.slate500,
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        boxShadow: "none",
                        transition: "box-shadow 140ms ease, transform 140ms ease",
                        "&:hover": {
                            boxShadow: shadow.sm,
                        },
                        "&:active": {
                            transform: "translateY(0)",
                        },
                        "&.Mui-disabled": {
                            opacity: 0.4,
                        },
                    },
                    sizeSmall: {
                        height: 32,
                        padding: "0 12px",
                    },
                    sizeMedium: {
                        height: 40,
                        padding: "0 20px",
                    },
                    sizeLarge: {
                        height: 48,
                        padding: "0 24px",
                    },
                    outlined: {
                        borderColor: color.slate300,
                    },
                },
                variants: [
                    {
                        // Reused by shared v1 dialogs (`Notificacion`) still rendered under `bpmV2Theme`
                        // (e.g. the "Cancelar" action of a CONFIRM dialog) — not part of the Lendia v2
                        // mockups, kept visually close to `BackOfficeTheme`'s `blue_outlined` but on v2 tokens.
                        props: { variant: "blue_outlined" },
                        style: {
                            color: color.navy800,
                            backgroundColor: color.white,
                            border: `2px solid ${color.navy800}`,
                            lineHeight: "22px",
                            "&:hover": {
                                backgroundColor: color.navy100,
                                border: `2px solid ${color.navy800}`,
                            },
                            "&.Mui-disabled": {
                                color: color.slate500,
                                border: `2px solid ${color.slate300}`,
                                backgroundColor: "transparent",
                            },
                        },
                    },
                ],
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.pill,
                        fontFamily: fontUi,
                        fontWeight: 700,
                        fontSize: "0.6875rem",
                    },
                    colorSuccess: {
                        backgroundColor: color.green100,
                        color: color.green600,
                    },
                    colorWarning: {
                        backgroundColor: color.amber100,
                        color: color.amber500,
                    },
                    colorError: {
                        backgroundColor: color.red100,
                        color: color.red500,
                    },
                    colorInfo: {
                        backgroundColor: color.navy100,
                        color: color.navy500,
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: "none",
                    },
                    rounded: {
                        borderRadius: radius.lg,
                    },
                    elevation1: {
                        border: `1px solid ${color.slate300}`,
                        boxShadow: shadow.card,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${color.slate300}`,
                        borderRadius: radius.lg,
                        boxShadow: shadow.card,
                        transition: "box-shadow 220ms ease, border-color 140ms ease",
                        "&:hover": {
                            boxShadow: shadow.md,
                            borderColor: color.navy300,
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        backgroundColor: color.white,
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: color.slate300,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: color.navy400,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: color.navy400,
                            boxShadow: shadow.focus,
                        },
                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: color.red500,
                        },
                    },
                    input: {
                        padding: "10px 12px",
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: color.slate500,
                        "&.Mui-focused": {
                            color: color.navy400,
                        },
                    },
                    asterisk: {
                        color: color.red500,
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: color.slate50,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: `1px solid ${color.slate200}`,
                        fontFamily: fontUi,
                        fontSize: "0.75rem",
                        padding: "10px 16px",
                    },
                    head: {
                        fontWeight: 700,
                        fontSize: "0.625rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        color: color.slate500,
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        "&:hover": {
                            backgroundColor: color.slate50,
                        },
                        "&:last-of-type td": {
                            borderBottom: "none",
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        backgroundColor: color.white,
                        border: `1px solid ${color.slate300}`,
                        borderRadius: radius.lg,
                        padding: 6,
                        minHeight: 0,
                    },
                    indicator: {
                        display: "none",
                    },
                    list: {
                        gap: 4,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontFamily: fontDisplay,
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        textTransform: "none",
                        color: color.slate500,
                        borderRadius: radius.md,
                        minHeight: 0,
                        padding: "11px 16px",
                        "&:hover": {
                            color: color.navy800,
                            backgroundColor: color.slate100,
                        },
                        "&.Mui-selected": {
                            color: color.white,
                            backgroundColor: color.navy800,
                            fontWeight: 600,
                            boxShadow: "0 2px 8px rgba(0,38,82,0.15)",
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: color.white,
                        color: color.navy800,
                        boxShadow: "none",
                        borderBottom: `1px solid ${color.slate300}`,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: color.white,
                        borderRight: `1px solid ${color.slate300}`,
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        borderLeft: "2px solid transparent",
                        "&:hover": {
                            backgroundColor: alpha(color.navy400, 0.06),
                            color: color.navy800,
                        },
                        "&.Mui-selected": {
                            backgroundColor: alpha(color.navy400, 0.08),
                            borderLeftColor: color.coral500,
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: alpha(color.navy400, 0.12),
                            },
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.md,
                        fontFamily: fontUi,
                        fontSize: "0.75rem",
                    },
                    colorSuccess: {
                        backgroundColor: color.green100,
                        color: color.green600,
                    },
                    colorWarning: {
                        backgroundColor: color.amber100,
                        color: color.amber500,
                    },
                    colorError: {
                        backgroundColor: color.red100,
                        color: color.red500,
                    },
                    colorInfo: {
                        backgroundColor: color.navy100,
                        color: color.navy500,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        backgroundColor: color.navy800,
                        color: color.white,
                        fontFamily: fontUi,
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        borderRadius: 6,
                        boxShadow: shadow.md,
                        padding: "6px 10px",
                    },
                    arrow: {
                        color: color.navy800,
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        height: 8,
                        borderRadius: radius.pill,
                        backgroundColor: color.slate200,
                    },
                    bar: {
                        borderRadius: radius.pill,
                    },
                },
            },
            MuiCheckbox: {
                defaultProps: {
                    color: "primary",
                },
            },
            MuiRadio: {
                defaultProps: {
                    color: "primary",
                },
            },
            MuiSwitch: {
                defaultProps: {
                    color: "primary",
                },
                styleOverrides: {
                    track: {
                        backgroundColor: color.slate300,
                        opacity: 1,
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        backgroundColor: color.navy100,
                        color: color.navy500,
                        fontFamily: fontDisplay,
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: radius.md,
                        border: `1px solid ${color.slate300}`,
                        boxShadow: shadow.lg,
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        margin: "2px 6px",
                        fontFamily: fontUi,
                        fontSize: "0.8125rem",
                        "&:hover": {
                            backgroundColor: alpha(color.navy400, 0.06),
                        },
                        "&.Mui-selected": {
                            backgroundColor: alpha(color.navy400, 0.1),
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: radius.xl,
                        boxShadow: shadow.lg,
                    },
                },
            },
            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        gap: 4,
                        padding: 6,
                        backgroundColor: color.white,
                        border: `1px solid ${color.slate300}`,
                        borderRadius: radius.lg,
                    },
                },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        border: "none",
                        borderRadius: radius.md,
                        fontFamily: fontDisplay,
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        textTransform: "none",
                        color: color.slate500,
                        padding: "6px 14px",
                        "&:hover": {
                            color: color.navy800,
                            backgroundColor: color.slate100,
                        },
                        "&.Mui-selected": {
                            color: color.white,
                            backgroundColor: color.navy800,
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: color.navy800,
                            },
                        },
                    },
                },
            },
            MuiPagination: {
                styleOverrides: {
                    root: {
                        fontFamily: fontUi,
                    },
                },
            },
            MuiPaginationItem: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.sm,
                        minWidth: 30,
                        height: 30,
                        fontFamily: fontUi,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: color.slate500,
                        "&:hover": {
                            backgroundColor: color.slate100,
                        },
                        "&.Mui-selected": {
                            color: color.white,
                            backgroundColor: color.navy800,
                            "&:hover": {
                                backgroundColor: color.navy800,
                            },
                        },
                    },
                },
            },
        },
    },
    esES,
);
